import { Dropdown, Menu, Tooltip } from 'antd';
import _ from 'lodash';
import { BulkAllActionIcon as bulkActionIcon } from '@/shared/ui';
import useBulkActionByEvent from './useBulkActionByEvent';
import './styles.scss';

export type BulkActionByEventProps = {
  isDisabled?: boolean;
  keyRecoil: string;
  activeButton: any;
  hasChanged?: boolean;
  className?: string;
};

/**
 * Offers a dropdown applying one action to every listed event, or to the filtered subset. Builds the menu items from the hook's bulk action list, renders nothing when there are no items or no holter events, and disables the trigger with an explanatory tooltip while the hide reviewed events filter is on.
 *
 * @param props - The bulk action props, supplying isDisabled and the wrapper className.
 * @returns The rendered bulk action dropdown, or null when there is nothing to act on.
 */
const BulkActionByEvent = ({
  isDisabled,
  keyRecoil,
  activeButton,
  hasChanged,
  className,
}: BulkActionByEventProps) => {
  const {
    eventFilter,
    holterEvents,
    bulkActionMenu,
    isFilteredSpecialEvent,
    isFilteredEvents,
    onClickItem,
  } = useBulkActionByEvent({ keyRecoil, activeButton, hasChanged });

  const items = _.map(bulkActionMenu, (x, i) => ({
    key: x.value,
    label: (
      <div id={`bulk-action-event-${i}`} className="bulk-action-radio">
        <div className="bulk-action-item --all-events">
          <div className="item-label">
            <span>{x.label}</span>
          </div>
        </div>
      </div>
    ),
  }));

  if (items.length === 0) {
    return null;
  }

  // if there is no events, we don't show bulk action menu
  if (_.isEmpty(holterEvents)) {
    return null;
  }

  return (
    <Dropdown
      trigger={['click']}
      disabled={isDisabled || eventFilter?.isHideReviewed}
      overlay={
        <div className="bulk-action-menu-wrapper">
          <div className="bulk-action-menu-desc">Do an action to all events:</div>
          <Menu onClick={onClickItem} className="bulk-action-menu" items={items} />
        </div>
      }
    >
      <Tooltip
        placement="bottom"
        title={
          eventFilter?.isHideReviewed
            ? 'Bulk event actions are unavailable when “Hide reviewed events” is on.'
            : null
        }
      >
        <div className={`bulk-action-by-event-wrapper ${className}`}>
          <button
            id="bulk-action-text--by-event"
            disabled={isDisabled || eventFilter?.isHideReviewed}
            className="bulk-action-text-by-event"
          >
            <img src={bulkActionIcon} alt="bulk action icon" />
            {isFilteredEvents || isFilteredSpecialEvent
              ? 'Filtered events actions'
              : 'All events actions'}
          </button>
        </div>
      </Tooltip>
    </Dropdown>
  );
};

export default BulkActionByEvent;
